import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, Bars3Icon, PencilSquareIcon, CheckIcon } from '@heroicons/react/24/outline';
import { ShowcaseSection, ShowcasePage } from '../../../types';

interface ReorderModalProps<T extends ShowcaseSection | ShowcasePage> {
  isOpen: boolean;
  onClose: () => void;
  items: T[];
  type: 'sections' | 'pages';
  onReorder: (reorderedItems: T[]) => void;
  getThumbnail?: (item: T) => string | null;
  onRename?: (item: T, newTitle: string) => void;
}

function ReorderModal<T extends ShowcaseSection | ShowcasePage>({ isOpen, onClose, items, type, onReorder, getThumbnail, onRename }: ReorderModalProps<T>) {
  const [orderedItems, setOrderedItems] = useState(items);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...orderedItems];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    
    setOrderedItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = () => {
    onReorder(orderedItems);
    onClose();
  };

  const startEditing = (item: T) => {
    setEditingId(item.id);
    setEditValue(item.title);
  };

  const saveEditing = (index: number) => {
    if (!editingId || !editValue.trim()) {
      setEditingId(null);
      return;
    }

    const newItems = [...orderedItems];
    const item = newItems[index];
    
    // Update local state
    newItems[index] = { ...item, title: editValue.trim() };
    setOrderedItems(newItems);
    
    // Call parent handler
    onRename?.(item, editValue.trim());
    
    setEditingId(null);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...orderedItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setOrderedItems(newItems);
  };

  const moveDown = (index: number) => {
    if (index === orderedItems.length - 1) return;
    const newItems = [...orderedItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setOrderedItems(newItems);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Manage {type === 'sections' ? 'Sections' : 'Pages'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Drag to reorder or click pencil to rename
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-2">
              {orderedItems.map((item, index) => {
                const thumbnail = getThumbnail?.(item);
                const isEditing = editingId === item.id;

                return (
                  <div
                    key={item.id}
                    draggable={!isEditing}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 p-4 bg-gray-50 rounded-xl border-2 transition-all ${
                      draggedIndex === index
                        ? 'border-teal-500 bg-teal-50 opacity-50'
                        : 'border-transparent hover:border-gray-200'
                    } ${!isEditing ? 'cursor-move' : ''}`}
                  >
                    <Bars3Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200 text-sm font-bold text-gray-700">
                      {index + 1}
                    </span>
                    {thumbnail && (
                      <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-200 border border-gray-300">
                        <img src={thumbnail} alt={item.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditing(index);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            className="w-full px-2 py-1 text-sm font-medium border border-teal-500 rounded focus:outline-none"
                            autoFocus
                          />
                          <button
                            onClick={() => saveEditing(index)}
                            className="p-1 text-teal-600 hover:bg-teal-50 rounded"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <span className="font-medium text-gray-900 truncate">{item.title}</span>
                          {onRename && (
                            <button
                              onClick={() => startEditing(item)}
                              className="p-1 text-gray-400 hover:text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        className="p-1.5 hover:bg-white rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveDown(index)}
                        disabled={index === orderedItems.length - 1}
                        className="p-1.5 hover:bg-white rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-teal-500 text-white rounded-lg font-bold hover:bg-teal-600 transition-colors shadow-lg shadow-teal-500/20"
              >
                Save Order
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ReorderModal;
